import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { 
    EmployePaymentSlips, 
    SignalEmployePaymentSlips,

    EmployeVacations, 
    SignalEmployeVacations,

    EmployeRecords,
    SignalEmployeRecords,
    EmployeContracts,
    SignalEmployeContracts,
    SignalEmployeCoupons,
    EmployeCoupons,
    SignalEmployeLoans,

} from '../../../interfaces/employeeDocuments.interface';
import { cleanDuplicates } from '../../../utils/data';
import { EMPLOYEE_CONTRACTS_ONE, EMPLOYEE_CONTRACTS_THREE, EMPLOYEE_CONTRACTS_TWO, EMPLOYEE_PAYMENT_SLIPS_ONE, EMPLOYEE_PAYMENT_SLIPS_THREE, EMPLOYEE_PAYMENT_SLIPS_TWO, EMPLOYEE_RECORDS_ONE, EMPLOYEE_RECORDS_THREE, EMPLOYEE_RECORDS_TWO, EMPLOYEE_LOANS_ONE, EMPLOYEE_LOANS_TWO, EMPLOYEE_LOANS_THREE } from '../../../constants/signalRequestConsts';

type Sort = "ASC" | "DESC";

type ContractsRequestId = typeof EMPLOYEE_CONTRACTS_ONE | typeof EMPLOYEE_CONTRACTS_TWO | typeof EMPLOYEE_CONTRACTS_THREE;
type PaymentSlipsRequestId = typeof EMPLOYEE_PAYMENT_SLIPS_ONE | typeof EMPLOYEE_PAYMENT_SLIPS_TWO | typeof EMPLOYEE_PAYMENT_SLIPS_THREE;
type RecordsRequestId = typeof EMPLOYEE_RECORDS_ONE | typeof EMPLOYEE_RECORDS_TWO | typeof EMPLOYEE_RECORDS_THREE;
type LoansRequestId = typeof EMPLOYEE_LOANS_ONE | typeof EMPLOYEE_LOANS_TWO | typeof EMPLOYEE_LOANS_THREE;

interface Contracts {
    contractsLoading: boolean;
    contracts: EmployeContracts[],
    contractsFirstSearch: boolean;
    contractsPartsReceive: number[];
    contractsSort: Sort | undefined;
    contractsSortField: string;
}

export interface Loans {
    loansLoading: boolean;
    loans: any[],
    loansFirstSearch: boolean;
    loansPartsReceive: number[];
    loansSort: Sort | undefined;
    loansSortField: string;
    totalItems: number;
}
interface PaymentSlips {
    paymentSlipsLoading: boolean;
    paymentSlips: EmployePaymentSlips[],
    paymentSlipsFirstSearch: boolean;
    paymentSlipsPartsReceive: number[];
    paymentSlipsSort: Sort | undefined;
    paymentSlipsSortField: string;
}

interface Records {
    recordsLoading: boolean;
    records: EmployeRecords[],
    recordsFirstSearch: boolean;
    recordsPartsReceive: number[];
    recordsSort: Sort | undefined;
    recordsSortField: string;
}

interface LoanCurrency {
    Base: boolean;
    CodMoneda: string;
    CodMonedaInternacional:string;
    Descripcion:string;
    Simbolo: string;
}

interface LoanType {
    Activo: any;
    ActualizacionFecha: string;
    ActualizacionUsuario: string;
    AuditorFecha: any;
    AuditorOK: any;
    AuditorUsuario: any;
    CodDocumento: string;
    CodIdentificador: any;
    CodInsumo: string;
    CodTipoPrestamo: string;
    ConceptoSaldo: string;
    CreacionFecha: string;
    CreacionUsuario: string;
    CuotaConceptoAmortizacion: string;
    CuotaConceptoPrestamo: string;
    CuotaDobleConceptoAmortizacion: string;
    CuotaDobleConceptoPrestamo: string;
    Descripcion: string;
    NroConcepto: string;
    NroConceptoAmortizacion: string;
    NroConceptoAmortizacionDoble: string;
    NroConceptoDoble: string;
    NroConceptoSaldo: string;
    NroTipoPrestamo: string;
    Recurso: string;
    SocioDeNegocio: any;
    TieneNota: any;
    TipoDocumento: string;
}

export interface LoanPeriod {
    NroProyectoEsquemaPeriodo: string;
    CodProyectoEsquemaPeriodo: string;
    Descripcion: string;
    Del: string;
    Al: string;
}

export interface LoadPaymentDetails {
    ActualizacionFecha: string
    ActualizacionUsuario: string
    Al: string
    CodProyectoEsquemaPeriodo: string
    MontoSaldo: number
    CreacionFecha: string
    CreacionUsuario: string
    CuotaDoble: any
    Del: string
    DescripcionPeriodo: string
    Estado: number
    MontoAmortizado: number
    MontoAmortizadoDoble: number
    MontoAmortizadoExtraordinaria: number
    MontoAmortizadoExtraordinariaOrigen: number
    MontoOtorgado: number
    MontoOtorgadoDoble: number
    MontoOtorgadoDobleOrigen: number
    MontoOtorgadoOrigen: number
    MontoSolicitado: number
    NroPrestamoPersonalCronograma: string
    NroProyectoEsquemaPeriodo: string
    NumeroCuota: number
}
export interface DocumentsSlice {

    signinRequestStatus: 'REQUESTING' | 'COMPLETED' | null,
    
    EMPLOYEE_PAYMENT_SLIPS_ONE: PaymentSlips;
    EMPLOYEE_PAYMENT_SLIPS_TWO: PaymentSlips;
    EMPLOYEE_PAYMENT_SLIPS_THREE: PaymentSlips;

    EMPLOYEE_RECORDS_ONE: Records;
    EMPLOYEE_RECORDS_TWO: Records;
    EMPLOYEE_RECORDS_THREE: Records;

    EMPLOYEE_CONTRACTS_ONE: Contracts;
    EMPLOYEE_CONTRACTS_TWO: Contracts;
    EMPLOYEE_CONTRACTS_THREE: Contracts;

    EMPLOYEE_LOANS_ONE: Loans;
    EMPLOYEE_LOANS_TWO: Loans;
    EMPLOYEE_LOANS_THREE: Loans;
    loans: {
        periods: LoanPeriod[];
        currencies: LoanCurrency[];
        types: LoanType[];
        paymentDetails: LoadPaymentDetails[];
        error: string;
        success: string;
        cancelSuccess: null | boolean;
        cancelMsg: null | string;
        requestResult: {
            success: boolean | null;
            msg: string | null;
            id: string | null;
        },
    }

    vacationsLoading: boolean;
    vacations: EmployeVacations[],
    vacationsFirstSearch: boolean;
    vacationsPartsReceive: number[];
    vacationsSort: Sort | undefined;
    vacationsSortField: string;


    cancelApprovedCouponResult?: {
        Success: 1 | 0
    }
    cancelApprovedCouponData?: EmployeCoupons;
    cancelApprovedCouponLoading: boolean;
    couponsLoading: boolean;
    couponsLoadingPending: boolean;
    coupons: EmployeCoupons[],
    couponsFirstSearch: boolean;
    couponsNumberOfPages: number;
    couponsTotalItems: number;

    couponsPendings: EmployeCoupons[],
    couponsPendingsFirstSearch: boolean;
    couponsPendingsNumberOfPages: number;
    couponsPendingsTotalItems: number;

}

const contractDefaultData = {
    contractsLoading: false,
    contracts: [],
    contractsFirstSearch: true,
    contractsPartsReceive: [],
    contractsSort: "DESC" as Sort,
    contractsSortField: "FechaEnviadoPortal",
}

const loansDefaultData = {
    loansLoading: false,
    loans: [],
    loansFirstSearch: true,
    loansPartsReceive: [],
    loansSort: "DESC" as Sort,
    loansSortField: "FechaEnviadoPortal",
    totalItems: 0,
}

const PaymentSlipsDefaultData = {
    paymentSlipsLoading: false,
    paymentSlips: [],
    paymentSlipsFirstSearch: true,
    paymentSlipsPartsReceive: [],
    paymentSlipsSort: "DESC" as Sort,
    paymentSlipsSortField: "FechaEnviadoPortal",
}

const RecordsDefaultData = {
    recordsLoading: false,
    records: [],
    recordsFirstSearch: true,
    recordsPartsReceive: [],
    recordsSort: "DESC" as Sort,
    recordsSortField: "FechaEnviadoPortal",
}

const initialState: DocumentsSlice = {
    signinRequestStatus: null,

    EMPLOYEE_PAYMENT_SLIPS_ONE: PaymentSlipsDefaultData,
    EMPLOYEE_PAYMENT_SLIPS_TWO: PaymentSlipsDefaultData,
    EMPLOYEE_PAYMENT_SLIPS_THREE: PaymentSlipsDefaultData,

    EMPLOYEE_RECORDS_ONE: RecordsDefaultData,
    EMPLOYEE_RECORDS_TWO: RecordsDefaultData,
    EMPLOYEE_RECORDS_THREE: RecordsDefaultData,

    vacationsLoading: false,
    vacations: [],
    vacationsFirstSearch: true,
    vacationsPartsReceive: [],
    vacationsSort: undefined,
    vacationsSortField: "",


    EMPLOYEE_CONTRACTS_ONE: contractDefaultData,
    EMPLOYEE_CONTRACTS_TWO: contractDefaultData,
    EMPLOYEE_CONTRACTS_THREE: contractDefaultData,

    EMPLOYEE_LOANS_ONE: loansDefaultData,
    EMPLOYEE_LOANS_TWO: loansDefaultData,
    EMPLOYEE_LOANS_THREE: loansDefaultData,
    loans: {
        periods: [],
        currencies: [],
        types: [],
        paymentDetails: [],
        error: "",
        success: "",
        cancelSuccess: null,
        cancelMsg: null,
        requestResult: {
            success: null,
            msg: null,
            id: null,
        },
    },

    cancelApprovedCouponLoading: false,
    couponsLoading: false,
    couponsLoadingPending: false,
    coupons: [],
    couponsFirstSearch: true,
    couponsNumberOfPages: 0,
    couponsTotalItems: 0,
    
    couponsPendings: [],
    couponsPendingsFirstSearch: true,
    couponsPendingsNumberOfPages: 0,
    couponsPendingsTotalItems: 0,
}




export const documentsSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setSigninRequestStatus: (state, {payload}: PayloadAction<"REQUESTING" | "COMPLETED" | null>) => {
            state.signinRequestStatus = payload;
        },
        moveColumnPaymentSlips: (state, {payload}: PayloadAction<{id: string, from: PaymentSlipsRequestId, to: PaymentSlipsRequestId}>) => {
            const paymentSlip = state[payload.from].paymentSlips.find(ps => ps.NroBoletaNomina === payload.id);
            if (!paymentSlip) {
                console.warn("payment slip #", payload.id, "not found");
                return;
            }
            state[payload.from].paymentSlips = state[payload.from].paymentSlips.filter(ps => ps.NroBoletaNomina !== payload.id);
            state[payload.to].paymentSlips = [paymentSlip, ...state[payload.to].paymentSlips].sort((a, b) => {
                const fechaA = new Date(a.FechaEnviadoPortal.split('/').reverse().join(' '));
                const fechaB = new Date(b.FechaEnviadoPortal.split('/').reverse().join(' '));
                return fechaB.getTime() - fechaA.getTime();
            });            
        },
        setLoadingPaymentSlips: (state, {payload}: PayloadAction<{loading: boolean, requestId: PaymentSlipsRequestId}>) => {
            state[payload.requestId].paymentSlipsFirstSearch = false;
            state[payload.requestId].paymentSlipsLoading = payload.loading;
        },
        setPaymentSlips: (state, {payload}: PayloadAction<SignalEmployePaymentSlips>) => {
            state[payload.requestId].paymentSlipsFirstSearch = false;
            if (state[payload.requestId].paymentSlipsPartsReceive.includes(payload.Part)) {
                console.log("this part is duplicated", payload.Part)
                return;
            }
            state[payload.requestId].paymentSlipsPartsReceive = [...state[payload.requestId].paymentSlipsPartsReceive, payload.Part];
            const paymentSlips = cleanDuplicates([...state[payload.requestId].paymentSlips, ...payload.Data], "NumeroDocumento")
            state[payload.requestId].paymentSlips = paymentSlips;
            if (state[payload.requestId].paymentSlipsPartsReceive.length === payload.Count) {
                state[payload.requestId].paymentSlipsLoading = false;
            }
        },
        setPaymentSlipsSort: (state, {payload}: PayloadAction<{sortField: string, sortType: Sort, rowData: EmployePaymentSlips[], requestId: PaymentSlipsRequestId}>) => {
            state[payload.requestId].paymentSlipsSort = payload.sortType;
            state[payload.requestId].paymentSlipsSortField = payload.sortField;
            state[payload.requestId].paymentSlips = payload.rowData;
        },
        resetSlipsSort: (state, {payload}: PayloadAction<PaymentSlipsRequestId>) => {
            state[payload].paymentSlipsSort = undefined;
            state[payload].paymentSlipsSortField = "";
            state[payload].paymentSlips = [];
            state[payload].paymentSlipsPartsReceive = [];
        },
        setPaymentSlipSignedDatetime: (state, {payload}: PayloadAction<{id: string, timestamp: string}>) => {
            const values = [EMPLOYEE_PAYMENT_SLIPS_ONE, EMPLOYEE_PAYMENT_SLIPS_TWO, EMPLOYEE_PAYMENT_SLIPS_THREE]
            for (let i = 0; i < values.length; i++) {
                const requestId = values[i] as PaymentSlipsRequestId;

                const documents = [...state[requestId].paymentSlips];
                console.log(payload)
                const index = documents.findIndex(d => d.NroBoletaNomina === payload.id);
                if (index >= 0) {
                    documents[index].FirmadoFecha = payload.timestamp;
                    state[requestId].paymentSlips = documents;

                } else {
                    console.log('not found', payload)
                }
            }
            if (state.signinRequestStatus === 'REQUESTING') state.signinRequestStatus = 'COMPLETED';
        },


        setLoadingVacations: (state, {payload}: PayloadAction<boolean>) => {
            state.vacationsFirstSearch = false;
            state.vacationsLoading = payload;
        },
        setVacations: (state, {payload}: PayloadAction<SignalEmployeVacations>) => {
            state.vacationsFirstSearch = false;
            if (state.vacationsPartsReceive.includes(payload.Part)) {
                console.log("this part is duplicated", payload.Part)
                return;
            }
            state.vacationsPartsReceive = [...state.vacationsPartsReceive, payload.Part];
            const vacations = cleanDuplicates([...state.vacations, ...payload.Data], "NroVacacionProgramada")
            state.vacations = vacations;
            if (state.vacationsPartsReceive.length === payload.Count) {
                state.vacationsLoading = false;
            }
        },
        setVacationsSort: (state, {payload}: PayloadAction<{sortField: string, sortType: Sort, rowData: EmployeVacations[]}>) => {
            state.vacationsSort = payload.sortType;
            state.vacationsSortField = payload.sortField;
            state.vacations = payload.rowData;
        },
        resetVacationsSort: (state) => {
            state.vacationsSort = undefined;
            state.vacationsSortField = "";
            state.vacations = [];
            state.vacationsPartsReceive = [];
        },

    
        setLoadingRecords: (state, {payload}: PayloadAction<{loading: boolean, requestId: RecordsRequestId}>) => {

            state[payload.requestId].recordsFirstSearch = false;
            state[payload.requestId].recordsLoading = payload.loading;
        },
        setRecords: (state, {payload}: PayloadAction<SignalEmployeRecords>) => {
            state[payload.requestId].recordsFirstSearch = false;
            if (state[payload.requestId].recordsPartsReceive.includes(payload.Part)) {
                console.log("this part is duplicated", payload.Part)
                return;
            }
            state[payload.requestId].recordsPartsReceive = [...state[payload.requestId].recordsPartsReceive, payload.Part];
            const records = cleanDuplicates([...state[payload.requestId].records, ...payload.Data], "NroBoletaNomina")
            state[payload.requestId].records = records;
            if (state[payload.requestId].recordsPartsReceive.length === payload.Count) {
                state[payload.requestId].recordsLoading = false;
            }
        },
        setRecordsSort: (state, {payload}: PayloadAction<{sortField: string, sortType: Sort, rowData: EmployeRecords[], requestId: RecordsRequestId}>) => {
            state[payload.requestId].recordsSort = payload.sortType;
            state[payload.requestId].recordsSortField = payload.sortField;
            state[payload.requestId].records = payload.rowData;
        },
        resetRecordsSort: (state, {payload}: PayloadAction<RecordsRequestId>) => {
            state[payload].recordsSort = undefined;
            state[payload].recordsSortField = "";
            state[payload].records = [];
            state[payload].recordsPartsReceive = [];
        },
        setRecordSignedDatetime: (state, {payload}: PayloadAction<{id: string, timestamp: string}>) => {
            const values = [EMPLOYEE_RECORDS_ONE, EMPLOYEE_RECORDS_TWO, EMPLOYEE_RECORDS_THREE]
            for (let i = 0; i < values.length; i++) {
                const requestId = values[i] as RecordsRequestId;
                const documents = [...state[requestId].records];
                const index = documents.findIndex(d => d.NroBoletaNomina === payload.id);
                if (index >= 0) {
                    documents[index].FirmadoFecha = payload.timestamp;
                    state[requestId].records = documents;
                } else {
                    console.log('not found', payload)
                }
            }
            if (state.signinRequestStatus === 'REQUESTING') state.signinRequestStatus = 'COMPLETED';

        },


        setLoadingContracts: (state, {payload}: PayloadAction<{loading: boolean, requestId: ContractsRequestId}>) => {
            state[payload.requestId].contractsFirstSearch = false;
            state[payload.requestId].contractsLoading = payload.loading;
        },
        setContracts: (state, {payload}: PayloadAction<SignalEmployeContracts>) => {
            state[payload.requestId].contractsFirstSearch = false;
            if (state[payload.requestId].contractsPartsReceive.includes(payload.Part)) {
                console.log("this part is duplicated", payload.Part)
                return;
            }
            state[payload.requestId].contractsPartsReceive = [...state[payload.requestId].contractsPartsReceive, payload.Part];
            const contracts = cleanDuplicates([...state[payload.requestId].contracts, ...payload.Data], "NroIdentificadorContrato")
            state[payload.requestId].contracts = contracts;
            if (state[payload.requestId].contractsPartsReceive.length === payload.Count) {
                state[payload.requestId].contractsLoading = false;
            }
        },
        setContractsSort: (state, {payload}: PayloadAction<{sortField: string, sortType: Sort, rowData: EmployeContracts[], requestId: ContractsRequestId}>) => {
            state[payload.requestId].contractsSort = payload.sortType;
            state[payload.requestId].contractsSortField = payload.sortField;
            state[payload.requestId].contracts = payload.rowData;
        },
        resetContractsSort: (state, {payload}: PayloadAction<ContractsRequestId>) => {
            state[payload].contractsSort = undefined;
            state[payload].contractsSortField = "";
            state[payload].contracts = [];
            state[payload].contractsPartsReceive = [];
        },
        setContractsSignedDatetime: (state, {payload}: PayloadAction<{id: string, timestamp: string}>) => {
            const values = [EMPLOYEE_CONTRACTS_ONE, EMPLOYEE_CONTRACTS_TWO, EMPLOYEE_CONTRACTS_THREE]
            for (let i = 0; i < values.length; i++) {
                const requestId = values[i] as ContractsRequestId;
                const documents = [...state[requestId].contracts];
                console.log(payload)
                console.log(documents)
                const index = documents.findIndex(d => d.NroIdentificadorContrato === payload.id);
                if (index >= 0) {
                    documents[index].FirmadoFecha = payload.timestamp;
                    state[requestId].contracts = documents;
                } else {
                    console.log('not found', payload)
                }
            }
            
            if (state.signinRequestStatus === 'REQUESTING') state.signinRequestStatus = 'COMPLETED';

        },
        setContractObservation:  (state, {payload}: PayloadAction<{NroIdentificadorContrato: String, observation: string,}>) => {
            console.log('contract observacion')
            console.log(payload)
            const values = [EMPLOYEE_CONTRACTS_ONE, EMPLOYEE_CONTRACTS_TWO, EMPLOYEE_CONTRACTS_THREE]
            for (let i = 0; i < values.length; i++) {
                const requestId = values[i] as ContractsRequestId;
                const documents = [...state[requestId].contracts];
                console.log(payload)
                const index = documents.findIndex(d => d.NroIdentificadorContrato === payload.NroIdentificadorContrato);
                if (index >= 0) {
                    documents[index].FirmadoRespuesta = payload.observation;
                    state[requestId].contracts = documents;
                } else {
                    console.log('not found', payload)
                }
            }
        },


        setLoancancelResult: (state, {payload}: PayloadAction<{success: boolean | null, msg: string | null}>) => {
            state.loans.cancelSuccess = payload.success;
            state.loans.cancelMsg = payload.success ? 'Cancelado correctamente' : payload.msg || 'Sin respuesta';
        },
        setLoadingLoans: (state, {payload}: PayloadAction<{loading: boolean, requestId: LoansRequestId}>) => {
            state[payload.requestId].loansFirstSearch = false;
            state[payload.requestId].loansLoading = payload.loading;
        },
        setLoansCurrencies: (state, {payload}: PayloadAction<any>) => {
            state.loans.currencies = payload;
        },
        setLoanRequestResult: (state, {payload}: PayloadAction<{success: boolean | null, msg: string | null, id?: string | null}>) => {
            const data = {
                success: payload.success,
                msg: payload.msg,
                id: payload.id || null,
            }
            state.loans.requestResult = data;
        },
        setLoanSuccess: (state, {payload}: PayloadAction<string>) => {
            state.loans.success = payload;
        },
        setLoanError: (state, {payload}: PayloadAction<string>) => {
            state.loans.error = payload;
        },
        setLoanPeriods: (state, {payload}: PayloadAction<any>) => {
            state.loans.periods = payload;
        },
        setLoansTypes: (state, {payload}: PayloadAction<any>) => {
            state.loans.types = payload;
        },
        setLoans: (state, {payload}: PayloadAction<SignalEmployeLoans>) => {
            state[payload.requestId].loansFirstSearch = false;
            if (state[payload.requestId].loansPartsReceive.includes(payload.Part)) {
                console.log("this part is duplicated", payload.Part)
                return;
            }
            state[payload.requestId].loansPartsReceive = [...state[payload.requestId].loansPartsReceive, payload.Part];
            const loans = cleanDuplicates([...state[payload.requestId].loans, ...payload.Data], "NroPrestamoPersonal")
            state[payload.requestId].loans = loans;
            if (state[payload.requestId].loansPartsReceive.length === payload.Count) {
                state[payload.requestId].loansLoading = false;
            }
            if (payload.outputValue) {
                state[payload.requestId].totalItems = Number(payload.outputValue.split("/")[1])
            }
        },
        setLoansSort: (state, {payload}: PayloadAction<{sortField: string, sortType: Sort, rowData: any[], requestId: LoansRequestId}>) => {
            state[payload.requestId].loansSort = payload.sortType;
            state[payload.requestId].loansSortField = payload.sortField;
            state[payload.requestId].loans = payload.rowData;
        },
        resetLoansSort: (state, {payload}: PayloadAction<LoansRequestId>) => {
            state[payload].loansSort = undefined;
            state[payload].loansSortField = "";
            state[payload].loans = [];
            state[payload].loansPartsReceive = [];
        },
        setLoansSignedDatetime: (state, {payload}: PayloadAction<{id: string, timestamp: string}>) => {
            const values = [EMPLOYEE_CONTRACTS_ONE, EMPLOYEE_CONTRACTS_TWO, EMPLOYEE_CONTRACTS_THREE]
            for (let i = 0; i < values.length; i++) {
                const requestId = values[i] as LoansRequestId;
                const documents = [...state[requestId].loans];
                console.log(payload)
                console.log(documents)
                const index = documents.findIndex(d => d.NroIdentificadorContrato === payload.id);
                if (index >= 0) {
                    documents[index].FirmadoFecha = payload.timestamp;
                    state[requestId].loans = documents;
                } else {
                    console.log('not found', payload)
                }
            }
            
            if (state.signinRequestStatus === 'REQUESTING') state.signinRequestStatus = 'COMPLETED';

        },
        setLoanDetails:  (state, {payload}: PayloadAction<any>) => {
            state.loans.paymentDetails = payload;
        },

        


        
        setCancelApprovedCouponResult: (state, {payload}: PayloadAction<{Success: 1 | 0} | undefined>) => {
            state.cancelApprovedCouponResult = payload;
        },
        setCancelApprovedCouponData: (state, {payload}: PayloadAction<EmployeCoupons | undefined>) => {
            state.cancelApprovedCouponData = payload;
        },
        setLoadingCancelApprovedCoupon: (state, {payload}: PayloadAction<boolean>) => {
            state.cancelApprovedCouponLoading = payload;
        },

        setLoadingCoupons: (state, {payload}: PayloadAction<{loading: boolean, onlyPending: boolean}>) => {
            state.couponsFirstSearch = false;
            if (payload.onlyPending) {
                state.couponsLoadingPending = payload.loading;
            } else {
                state.couponsLoading = payload.loading;
            }
        },
        setCoupons: (state, {payload}: PayloadAction<SignalEmployeCoupons>) => {
            state.couponsFirstSearch = false;
            if (!payload.onlyPending) {
                state.coupons = payload.Data;
                state.couponsNumberOfPages = payload.Pages;
                state.couponsTotalItems = payload.Count;
            } else {
                state.couponsPendings = payload.Data;
                state.couponsPendingsNumberOfPages = payload.Pages;
                state.couponsPendingsTotalItems = payload.Count;
            }

            if (payload.onlyPending) {
                state.couponsLoadingPending = false;
            } else {
                state.couponsLoading = false;
            }
            state.couponsLoading = false;
        },
        resetCouponsSort: (state) => {
            state.coupons = [];
            state.couponsNumberOfPages = 0;
        },



        setLoadingContacts: (state, {payload}: PayloadAction<boolean>) => {
            state.couponsFirstSearch = false;
            state.couponsLoading = payload;
        },
        setContacts: (state, {payload}: PayloadAction<SignalEmployeCoupons>) => {
            state.couponsFirstSearch = false;
            state.coupons = payload.Data;
            state.couponsNumberOfPages = payload.Pages;
            state.couponsTotalItems = payload.Count;
            state.couponsLoading = false;
        },
        resetContactsSort: (state) => {
            state.coupons = [];
            state.couponsNumberOfPages = 0;
        },
    }
})


export const { 
    setSigninRequestStatus,
    setLoadingPaymentSlips,
    moveColumnPaymentSlips,
    setPaymentSlips,
    setPaymentSlipsSort,
    resetSlipsSort,
    setPaymentSlipSignedDatetime,

    setLoadingVacations,
    setVacations,
    setVacationsSort,
    resetVacationsSort,

    setLoadingRecords,
    setRecords,
    setRecordsSort,
    resetRecordsSort,
    setRecordSignedDatetime,

    setLoadingContracts,
    setContracts,
    setContractsSort,
    resetContractsSort,
    setContractsSignedDatetime,
    setContractObservation,

    setLoanRequestResult,
    setLoancancelResult,
    setLoanPeriods,
    setLoadingLoans,
    setLoansCurrencies,
    setLoansTypes,
    setLoans,
    setLoansSort,
    resetLoansSort,
    setLoansSignedDatetime,
    setLoanDetails,
    setLoanError,
    setLoanSuccess,

    setCancelApprovedCouponResult,
    setCancelApprovedCouponData,
    setLoadingCancelApprovedCoupon,
    setLoadingCoupons,
    setCoupons,
    resetCouponsSort,

} = documentsSlice.actions
